import consentCheck from '@capsule/consent-check'
import Observer from '@capsule/observer'

export default class ArticleLive {
	wrapper: HTMLElement | null
	wrapperHeader: HTMLElement | null
	articleLiveId: string | null
	posts: HTMLCollection | undefined
	postsWrapper: HTMLElement | null
	liveStatusButton: HTMLElement | null
	liveStatusButtonText: HTMLElement | null
	seeMoreButtonWrapper: HTMLElement | null
	seeMoreButton: HTMLElement | null
	liveIsActive: boolean
	postsCursor: string | null
	newPosts: string
	pollingInterval: number
	newPostsCount: number

	constructor() {
		this.wrapper = document.querySelector('#articleLive')
		this.wrapperHeader = this.wrapper?.querySelector('.articleLive-head') ?? null
		this.postsWrapper = this.wrapper?.querySelector('.articleLive-posts') ?? null
		this.posts = this.postsWrapper?.children
		this.liveStatusButton = this.wrapper?.querySelector('.articleLive-buttonStatus') ?? null
		this.liveStatusButtonText = this.liveStatusButton?.querySelector('.cta-text') ?? null
		this.seeMoreButtonWrapper = this.wrapper?.querySelector('#articleLive-seeMore') ?? null
		this.seeMoreButton =
			this.seeMoreButtonWrapper?.querySelector('#articleLive-buttonSeeMore') ?? null

		this.articleLiveId = this.wrapper?.getAttribute('data-articleId') ?? null
		this.liveIsActive = this.wrapper?.getAttribute('data-liveActive') === 'true'
		this.postsCursor = this.wrapper?.getAttribute('data-cursor') ?? null
		this.newPosts = ''
		this.newPostsCount = 0
		this.pollingInterval = 1 * 60 * 1000 // 1 minute

		this.checkForNewPosts = this.checkForNewPosts.bind(this)
		this.renderNewPosts = this.renderNewPosts.bind(this)
		this.resetPostsState = this.resetPostsState.bind(this)
		this.seeMorePostsLive = this.seeMorePostsLive.bind(this)
		this.updateLiveStatusButton = this.updateLiveStatusButton.bind(this)
		this.injectNewPostsIntoDOM = this.injectNewPostsIntoDOM.bind(this)
		this.onLiveStatusButtonClick = this.onLiveStatusButtonClick.bind(this)
	}

	async init() {
		consentCheck('allConsentGiven', () => {
			if (!this.postsWrapper) return
			this.onPostsIntersection(this.postsWrapper)
		})

		this.addEvents()
		this.setLiveStatusButtonStickyness()

		if (this.wrapperHeader) {
			/* Avoid CLS when liveStatusButton CSS position's property change to 'fixed' */
			this.wrapperHeader.style.height = `${this.wrapperHeader?.clientHeight}px`
		}
	}

	addEvents() {
		this.seeMoreButton?.addEventListener('click', this.seeMorePostsLive)

		if (this.liveIsActive) {
			this.liveStatusButton?.addEventListener('click', this.onLiveStatusButtonClick)

			window.setInterval(this.checkForNewPosts, this.pollingInterval)
		}
	}

	/**
	 * onLiveStatusButtonClick
	 */
	onLiveStatusButtonClick() {
		this.renderNewPosts()

		const wrapperHeaderOffsetY = this.wrapperHeader?.getBoundingClientRect().y ?? 0
		if (wrapperHeaderOffsetY < 0) {
			this.wrapperHeader?.scrollIntoView()
		}
	}

	/**
	 * Checks for new posts in the live article.
	 * If new posts are found, updates the vars and buttonStatus UI,
	 */
	async checkForNewPosts() {
		const newPosts = await this.getNewPosts()

		if (newPosts) {
			this.newPostsCount += Number(newPosts.length)
			this.postsCursor = newPosts.cursor
			this.newPosts = newPosts.htmlString + this.newPosts
			this.updateLiveStatusButton()
		}
	}

	/**
	 * If .articleLive-head is not in viewport, the refresh button become sticky
	 */
	setLiveStatusButtonStickyness() {
		if (this.wrapperHeader) {
			const observer = new IntersectionObserver(([header]) => {
				if (header.target.getBoundingClientRect().y < 0) {
					this.liveStatusButton?.classList.toggle('isSticky', !header.isIntersecting)
				}
			})

			observer.observe(this.wrapperHeader)
		}
	}

	/**
	 * getNewPosts
	 * fetch newPosts and return formatted object
	 */
	async getNewPosts() {
		try {
			const response = await fetch(this.endPoint)

			return {
				length: response.headers.get('x-post-count'),
				cursor: response.headers.get('x-post-cursor'),
				htmlString: (await response.text()) ?? ''
			}
		} catch (error) {
			console.error('[LivePosts] Error fetching new posts:', error)
		}
	}

	/**
	 * RenderNewPosts
	 * Update UI with new posts and reset state
	 */
	async renderNewPosts() {
		if (!this.postsWrapper) return

		if (document.startViewTransition) {
			const transition = document.startViewTransition(this.injectNewPostsIntoDOM)
			await transition.ready
		} else {
			this.injectNewPostsIntoDOM()
		}

		this.resetPostsState()
	}

	/**
	 * injectNewPostsIntoDOM
	 */
	injectNewPostsIntoDOM() {
		if (!this.postsWrapper || !this.newPosts) return
		this.postsWrapper?.insertAdjacentHTML('afterbegin', this.newPosts)
	}

	/**
	 * resetPostsState
	 * Once use clicked on refresh button, reset all to the 'up to date' state
	 */
	resetPostsState() {
		this.newPosts = ''
		this.newPostsCount = 0
		this.updateLiveStatusButton()
		this.setPostsViewTransitionName()
	}

	/**
	 * setPostsViewTransitionName
	 * Once the new posts are injected, reset all viewTransitionName
	 */
	setPostsViewTransitionName() {
		if (this.posts) {
			;[...this.posts].forEach((post, index) => {
				if (post instanceof HTMLElement) {
					post.style.viewTransitionName = `livepost-${index + 1}`
				}
			})
		}
	}

	/**
	 * seeMorePostsLive
	 * Show the next 20 hidden posts if exists
	 */
	seeMorePostsLive() {
		if (!this.posts) return

		const hiddenPosts = [...this.posts]
			.filter((post) => post.hasAttribute('hidden'))
			.slice(0, 20)

		for (const post of hiddenPosts) {
			post.removeAttribute('hidden')
		}

		const remainingHiddenPosts = [...this.posts].filter((post) => post.hasAttribute('hidden'))

		if (remainingHiddenPosts.length === 0) {
			this.hideButtonSeeMore()
		}
	}

	/**
	 * hideButtonSeeMore
	 */
	hideButtonSeeMore() {
		return this.seeMoreButtonWrapper?.setAttribute('hidden', '')
	}

	/**
	 * onPostsIntersection
	 * @param { Element } livePosts
	 */
	onPostsIntersection(livePosts: Element): void {
		const observer = new Observer({
			elements: livePosts,
			onIntersection: this.initEmbedLiveScripts
		})

		observer.observe()
	}

	/**
	 * initEmbedLiveScripts
	 */
	initEmbedLiveScripts(): void {
		const dataEmbedLive = document.getElementById('template-embedLiveScript')

		if (dataEmbedLive instanceof HTMLTemplateElement) {
			const embedLiveScript = dataEmbedLive.content.cloneNode(true)
			document.body.appendChild(embedLiveScript)
			dataEmbedLive.remove()
		}
	}

	/**
	 * updateLiveStatusButton
	 * Update text and how many new posts are avaible
	 */
	async updateLiveStatusButton() {
		if (!this.liveStatusButtonText || !this.liveStatusButton) return

		this.liveStatusButton.classList.toggle('hasNewPosts', !!this.newPostsCount)

		this.liveStatusButtonText.textContent = this.liveStatusButton?.getAttribute(
			this.newPostsCount ? 'data-onNewPostsText' : 'data-onNoNewPostsText'
		)

		this.liveStatusButtonText.setAttribute(
			'data-newPostCount',
			String(this.newPostsCount > 999 ? '999+' : this.newPostsCount)
		)
	}

	/**
	 * endPoint
	 * Compute the endpoint
	 * @readonly
	 * @type {string}
	 */
	get endPoint() {
		if (!this.articleLiveId) throw new Error('Article Live ID is not set.')
		if (!this.postsCursor) throw new Error('Article Live Cursor is not set.')

		return `/_xhr/live-article-posts/${this.articleLiveId}?cursor=${this.postsCursor}`
	}
}
